import React, { useState, useEffect, useMemo, useContext } from "react";
import { Link } from "react-router-dom";
import { AdminSiteContext } from "../contexts/AdminSiteContext";
//
//MUI Components
import { makeStyles } from "@material-ui/core/styles";
import { DataGrid, GridToolbar } from "@material-ui/data-grid";
import Typography from "@material-ui/core/Typography";
import { Button, Tooltip } from "@material-ui/core";
import { IconButton } from "@material-ui/core";
import DeleteIcon from "@material-ui/icons/Delete";
import AddIcon from "@material-ui/icons/Add";
import EditIcon from "@material-ui/icons/Edit";

const useStyles = makeStyles((theme) => ({
  datagrid: {
    backgroundColor: "white",
    //Remove outlines when clicking a cell in the datagrid
    "&.MuiDataGrid-root .MuiDataGrid-cell:focus-within": {
      outline: 0,
    },
    //Remove outlines when clicking a column header
    "&.MuiDataGrid-root .MuiDataGrid-colCell:focus": {
      outline: 0,
    },
  },
  slugLink: {
    color: theme.palette.primary.main,
  },
}));

export default function Entries() {
  const classes = useStyles();
  const [rows, setRows] = useState([]);
  const [selectionModel, setSelectionModel] = useState([]);
  const [sortModel, setSortModel] = useState([{ field: "title", sort: "asc" }]);
  const { adminSelectedSite } = useContext(AdminSiteContext);

  useEffect(() => {
    const getRows = async () => {
      let rowsFromServer = await fetchRows();
      rowsFromServer = rowsFromServer.data;
      if (adminSelectedSite && adminSelectedSite._id) {
        rowsFromServer = rowsFromServer.filter(
          (row) => row.unit.site._id === adminSelectedSite._id
        );
      }
      setRows(rowsFromServer);
    };
    getRows();
  }, [adminSelectedSite]);

  const fetchRows = async () => {
    const res = await fetch(`${process.env.REACT_APP_API_URL}/entries`);
    const data = await res.json();
    return data;
  };

  const deleteFromAPI = async (id) => {
    try {
      await fetch(`${process.env.REACT_APP_API_URL}/entries/${id}`, {
        method: "DELETE",
        credentials: "include",
      });
    } catch (e) {
      console.log(e);
    }
  };

  const onClickDeleteAll = () => {
    selectionModel.map((id) => deleteFromAPI(id));

    //Remove selected items from the rows state
    let newRows = [...rows];
    for (let i = 0; i < selectionModel.length; i++) {
      newRows = newRows.filter((row) => row._id !== selectionModel[i]);
    }
    setRows(newRows);

    //Clear selection
    setSelectionModel([]);
  };

  function getRelatedEntries(params) {
    return params.row.relatedEntries.length ?? null;
  }

  function getRelatedPeople(params) {
    return params.row.relatedPeople.length ?? null;
  }

  const columns = useMemo(
    () => [
      { field: "_id", headerName: "ID", hide: true, width: 225 },
      {
        field: "title",
        headerName: "Title",
        renderCell: (params) => (
          <React.Fragment>
            <IconButton
              component={Link}
              to={`/${process.env.REACT_APP_ADMIN_URL}/entries/${params.row.id}`}
              style={{ marginRight: ".5rem" }}
              aria-label="edit"
            >
              <EditIcon />
            </IconButton>
            <Tooltip title={params.value} placement="bottom-start">
              <p>{params.value}</p>
            </Tooltip>
          </React.Fragment>
        ),
        width: 500,
      },
      {
        field: "published",
        headerName: "Status",
        valueGetter: (params) => {
          if (params.value) {
            return "Published";
          } else {
            return "Draft";
          }
        },
        renderCell: (params) => {
          if (params.value === "Published") {
            return (
              <Typography variant="body2" color="primary">
                <strong>{params.value}</strong>
              </Typography>
            );
          } else {
            return (
              <Typography variant="body2" color="secondary">
                <strong>{params.value}</strong>
              </Typography>
            );
          }
        },
        width: 150,
      },
      {
        field: "slug",
        headerName: "Slug",
        width: 500,
        renderCell: (params) => {
          return (
            <Tooltip
              title={`/${params.row.unit.site.slug}/entries/${params.row.slug}`}
              placement="bottom-start"
            >
              <Typography component="span" variant="body2">
                <Link
                  className={classes.slugLink}
                  to={`/${params.row.unit.site.slug}/entries/${params.row.slug}`}
                  target="_blank"
                >
                  {params.row.slug}
                </Link>
              </Typography>
            </Tooltip>
          );
        },
      },
      {
        field: "relatedPeople",
        headerName: "Related People",
        valueGetter: getRelatedPeople,
        type: "number",
        width: 180,
      },
      {
        field: "relatedEntries",
        headerName: "Related Entries",
        valueGetter: getRelatedEntries,
        type: "number",
        width: 180,
      },
    ],
    [classes.slugLink]
  );

  return (
    <>
      <div
        style={{ display: "flex", alignItems: "center", marginBottom: "1rem" }}
      >
        <Typography component="h1" variant="h4">
          Entries
          {adminSelectedSite &&
            adminSelectedSite !== "" &&
            ` in ${adminSelectedSite.name}`}
        </Typography>
        <Button
          startIcon={<AddIcon />}
          style={{ marginLeft: "1rem" }}
          color="primary"
          variant="contained"
          component={Link}
          to={`/${process.env.REACT_APP_ADMIN_URL}/entries/add`}
        >
          Add New
        </Button>
        {selectionModel.length > 0 && (
          <Button
            startIcon={<DeleteIcon />}
            style={{ marginLeft: "1rem" }}
            color="secondary"
            variant="contained"
            onClick={onClickDeleteAll}
          >
            Delete
          </Button>
        )}
      </div>
      <DataGrid
        className={classes.datagrid}
        components={{
          Toolbar: GridToolbar,
        }}
        getRowId={(row) => row._id}
        autoHeight
        rows={rows}
        columns={columns}
        checkboxSelection
        sortModel={sortModel}
        onSortModelChange={(model) => setSortModel(model)}
        selectionModel={selectionModel}
        onSelectionModelChange={(newSelection) => {
          setSelectionModel(newSelection);
        }}
      />
    </>
  );
}
